import React, { useState, useEffect, useRef } from 'react';
import { themes, applyTheme } from './themes';

interface ThemeSelectorProps {
  currentTheme: string;
  onThemeChange: (themeName: string) => void;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ currentTheme, onThemeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleThemeSelect = (themeName: string) => {
    applyTheme(themeName);
    onThemeChange(themeName);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'var(--button-bg)',
          color: 'var(--button-text)',
          border: 'none',
          padding: '0.5rem 1rem',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '0.9rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'all 0.2s'
        }}
        onMouseOver={(e) => e.currentTarget.style.background = 'var(--button-hover)'}
        onMouseOut={(e) => e.currentTarget.style.background = 'var(--button-bg)'}
      >
        🎨 Theme
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            background: 'var(--card-bg)',
            border: `2px solid var(--card-border)`,
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            minWidth: '220px',
            zIndex: 1000,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '0.75rem', borderBottom: `1px solid var(--card-border)` }}>
            <h3 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: '600' }}>
              Choose Theme
            </h3>
          </div>
          <div style={{ padding: '0.5rem' }}>
            {Object.entries(themes).map(([key, theme]) => (
              <button
                key={key}
                onClick={() => handleThemeSelect(key)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: currentTheme === key ? 'var(--accent)' : 'transparent',
                  color: currentTheme === key ? 'var(--button-text)' : 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  textAlign: 'left',
                  marginBottom: '0.25rem',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  if (currentTheme !== key) {
                    e.currentTarget.style.background = 'var(--input-bg)';
                  }
                }}
                onMouseOut={(e) => {
                  if (currentTheme !== key) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      background: theme.colors.accent,
                      border: '2px solid var(--card-border)'
                    }}
                  />
                  {theme.displayName}
                </span>
                {currentTheme === key && <span>✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
